import { Button } from '@/components/ui/button'
import { useModal } from '@/contexts/modal'
import HelpIcons from '@/icons/HelpIcons'
import { cn } from '@/lib/utils'
import { ReactNode, useId } from 'react'
import { FieldValues, UseFormReturn } from 'react-hook-form'
import { Typogaphy } from 'vnetwork-ui'
import { FormField } from './form-field'
import type { FormFieldHelpModal, FormFieldProps } from './types'

/** Distributive Omit — preserves union members instead of collapsing them */
type DistributiveOmit<T, K extends keyof any> = T extends any
    ? Omit<T, K>
    : never

export interface DatacentralHookFormAction {
    /** Button type */
    type?: 'button' | 'submit'
    /** Button variant */
    variant?: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost'
    /** Click handler */
    handler?: () => void
    /** Icon at start */
    startIcon?: ReactNode
    /** Disabled state */
    disabled?: boolean
    /** Button label */
    title: string
    /** Loading state */
    loading?: boolean
}

export interface DatacentralHookFormHead {
    title: string
    icon?: ReactNode
    disabledAction?: boolean
    loadingAction?: boolean
}

export interface DatacentralHookFormDataItem<T extends FieldValues> {
    /** FormField props (without control) */
    formItemProps?: DistributiveOmit<FormFieldProps<T>, 'control'>
    /** Hide this item */
    hidden?: boolean
    /** Custom render component (escape hatch for unsupported field types) */
    renderEditComponent?: (control: UseFormReturn<T>['control']) => ReactNode
    /** Help button that opens a modal — wraps the field content */
    helpModal?: FormFieldHelpModal
    /** CSS class for grid item */
    className?: string
    /** Column span (1-12) */
    colSpan?: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12
}

export interface DatacentralHookFormProps<T extends FieldValues> {
    /** Form data items */
    data: DatacentralHookFormDataItem<T>[]
    /** Grid className */
    className?: string
    /** Form header */
    head?: DatacentralHookFormHead
    /** Action buttons */
    actions?: DatacentralHookFormAction[]
    /** react-hook-form instance */
    formControl: UseFormReturn<T>
    /** Submit handler */
    onSubmit: (values: T) => void
    /** Disable all fields */
    disabled?: boolean
    /** Render as div instead of form (for nested forms) */
    subForm?: boolean
    /** Grid columns (default: 4) */
    columns?: 2 | 3 | 4 | 6 | 12
}

const colSpanClasses: Record<number, string> = {
    1: 'col-span-1',
    2: 'col-span-2',
    3: 'col-span-3',
    4: 'col-span-4',
    5: 'col-span-5',
    6: 'col-span-6',
    7: 'col-span-7',
    8: 'col-span-8',
    9: 'col-span-9',
    10: 'col-span-10',
    11: 'col-span-11',
    12: 'col-span-12',
}

const gridColsClasses: Record<number, string> = {
    2: 'grid-cols-2',
    3: 'grid-cols-3',
    4: 'grid-cols-4',
    6: 'grid-cols-6',
    12: 'grid-cols-12',
}

/** Wraps content with a help button that opens a modal */
function HelpModalWrapper({
    helpModal,
    children,
}: {
    helpModal: FormFieldHelpModal
    children: ReactNode
}) {
    const { setModal } = useModal()
    return (
        <div className="relative">
            {children}
            <button
                type="button"
                onClick={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    setModal(helpModal.modalName)
                }}
                className="absolute right-0.5 -top-1 text-primary hover:text-primary rounded-full bg-white shadow-sm transition-colors z-10"
                title={helpModal.title}
            >
                <HelpIcons className="w-3 h-3" />
            </button>
        </div>
    )
}

export function DatacentralHookForm<T extends FieldValues>({
    data,
    className,
    head,
    actions,
    formControl,
    onSubmit,
    disabled,
    subForm,
    columns = 4,
}: DatacentralHookFormProps<T>) {
    const formId = useId()
    const Wrapper = subForm ? 'div' : 'form'

    /** Optionally wrap content with help modal button */
    const wrapWithHelp = (
        content: ReactNode,
        helpModal?: FormFieldHelpModal
    ) =>
        helpModal ? (
            <HelpModalWrapper helpModal={helpModal}>{content}</HelpModalWrapper>
        ) : (
            content
        )

    const gridContent = (
        <>
            {/* Header */}
            {head && (
                <div className="flex items-center gap-2 mb-4">
                    {head.icon}
                    <Typogaphy className="text-lg font-medium text-primary capitalize">
                        {head.title}
                    </Typogaphy>
                </div>
            )}

            {/* Grid */}
            <div
                className={cn(
                    'grid gap-4',
                    gridColsClasses[columns],
                    className
                )}
            >
                {data.map((item, index) => {
                    if (item.hidden) return null

                    const colSpan = item.colSpan || columns
                    const itemClassName = cn(
                        colSpanClasses[colSpan],
                        item.className
                    )

                    // Custom render
                    if (item.renderEditComponent) {
                        return (
                            <div key={index} className={itemClassName}>
                                {wrapWithHelp(
                                    item.renderEditComponent(
                                        formControl.control
                                    ),
                                    item.helpModal
                                )}
                            </div>
                        )
                    }

                    // FormField render
                    if (item.formItemProps) {
                        return (
                            <div key={index} className={itemClassName}>
                                {wrapWithHelp(
                                    <FormField
                                        {...(item.formItemProps as FormFieldProps<T>)}
                                        control={formControl.control}
                                        disabled={
                                            disabled ||
                                            item.formItemProps.disabled
                                        }
                                    />,
                                    item.helpModal
                                )}
                            </div>
                        )
                    }

                    return null
                })}
            </div>
        </>
    )

    /** Action buttons — rendered outside <form> to avoid submit interference */
    const actionButtons = actions && actions.length > 0 && (
        <div className="flex justify-end gap-2 mt-4">
            {actions.map((action, index) => (
                <Button
                    key={index}
                    type={action.type === 'submit' ? 'submit' : 'button'}
                    form={action.type === 'submit' ? formId : undefined}
                    variant={action.variant || 'outline'}
                    onClick={(e) => {
                        if (action.type !== 'submit') {
                            e.preventDefault()
                            e.stopPropagation()
                        }
                        action.handler?.()
                    }}
                    disabled={
                        action.disabled ||
                        action.loading ||
                        head?.disabledAction ||
                        head?.loadingAction
                    }
                >
                    {action.startIcon && (
                        <span className="mr-2">{action.startIcon}</span>
                    )}
                    {action.loading ? 'Loading...' : action.title}
                </Button>
            ))}
        </div>
    )

    if (subForm) {
        return (
            <div>
                {gridContent}
                {actionButtons}
            </div>
        )
    }

    return (
        <div>
            <Wrapper id={formId} onSubmit={formControl.handleSubmit(onSubmit)}>
                {gridContent}
            </Wrapper>
            {actionButtons}
        </div>
    )
}
